import * as PIXI from "pixi.js";
import type { MapConfig } from "./config.js";
import { scaleToZoom } from "./config.js";
import { TileCache, tileKeyStr } from "./tile-cache.js";
import type { Camera } from "./camera.js";

/**
 * Tile Manager - handles viewport culling, async tile loading,
 * parent-tile fallback, and abort-on-scroll-away.
 *
 * At each frame, determines which tiles are visible, initiates
 * fetches for missing tiles, and displays parent tiles as placeholders
 * while children load.
 */

interface TileSprite {
  sprite: PIXI.Sprite;
  key: string;
  z: number;
  x: number;
  y: number;
}

interface PendingFetch {
  abort: AbortController;
  key: string;
}

export class TileManager {
  private config: MapConfig;
  private cache: TileCache;
  private container: PIXI.Container;
  private activeTiles = new Map<string, TileSprite>();
  private pendingFetches = new Map<string, PendingFetch>();

  /** Max concurrent tile fetches. */
  private maxConcurrent = 6;

  /** Retry config. */
  private maxRetries = 2;
  private retryDelayMs = 500;

  /** Set of tile keys that have permanently failed (after all retries). */
  private failedTiles = new Set<string>();

  constructor(config: MapConfig, container: PIXI.Container) {
    this.config = config;
    this.cache = new TileCache(config.tileCacheSize);
    this.container = container;
  }

  /**
   * Called each frame. Determines visible tiles, loads missing ones,
   * and manages sprites in the container.
   */
  update(camera: Camera, screenW: number, screenH: number): void {
    const scale = camera.scale;
    const zoom = this.computeZoom(scale);
    const tilesAtZoom = Math.pow(2, zoom);
    const tileWorldSize = this.config.worldSize / tilesAtZoom;

    // Compute visible world bounds
    const worldLeft = -camera.x / scale;
    const worldTop = -camera.y / scale;
    const worldRight = worldLeft + screenW / scale;
    const worldBottom = worldTop + screenH / scale;

    // Determine visible tile range (with 1-tile margin for smooth scrolling)
    const txMin = Math.max(0, Math.floor(worldLeft / tileWorldSize) - 1);
    const tyMin = Math.max(0, Math.floor(worldTop / tileWorldSize) - 1);
    const txMax = Math.min(
      tilesAtZoom - 1,
      Math.floor(worldRight / tileWorldSize) + 1,
    );
    const tyMax = Math.min(
      tilesAtZoom - 1,
      Math.floor(worldBottom / tileWorldSize) + 1,
    );

    // Track which tiles should be visible this frame
    const neededKeys = new Set<string>();

    for (let ty = tyMin; ty <= tyMax; ty++) {
      for (let tx = txMin; tx <= txMax; tx++) {
        const key = tileKeyStr(zoom, tx, ty);
        neededKeys.add(key);

        // Already have a sprite for this tile
        if (this.activeTiles.has(key)) continue;

        // Try cache first
        const cached = this.cache.get(key);
        if (cached) {
          this.addTileSprite(key, zoom, tx, ty, cached, tileWorldSize);
          continue;
        }

        // Initiate fetch if not already pending
        if (!this.pendingFetches.has(key) && !this.failedTiles.has(key)) {
          this.fetchTile(zoom, tx, ty, tileWorldSize);
        }

        // Show parent tile as fallback while loading
        this.showFallbackParent(zoom, tx, ty, tileWorldSize);
      }
    }

    // Remove tiles no longer visible
    for (const [key, tile] of this.activeTiles) {
      if (!neededKeys.has(key) && !this.isParentFallback(key, neededKeys)) {
        this.container.removeChild(tile.sprite);
        tile.sprite.destroy();
        this.activeTiles.delete(key);
      }
    }

    // Abort fetches for tiles no longer needed
    for (const [key, pending] of this.pendingFetches) {
      if (!neededKeys.has(key)) {
        pending.abort.abort();
        this.pendingFetches.delete(key);
      }
    }
  }

  /** Compute the discrete zoom level from the current camera scale. */
  private computeZoom(scale: number): number {
    const continuous = scaleToZoom(scale, this.config);
    // Floor at 1: zoom-0 tiles don't exist in the tile pyramid.
    return Math.max(
      1,
      Math.min(this.config.maxZoom, Math.round(continuous)),
    );
  }

  /** Create and add a tile sprite to the container. */
  private addTileSprite(
    key: string,
    z: number,
    x: number,
    y: number,
    texture: PIXI.Texture,
    tileWorldSize: number,
  ): void {
    // Remove existing sprite if any
    const existing = this.activeTiles.get(key);
    if (existing) {
      this.container.removeChild(existing.sprite);
      existing.sprite.destroy();
    }

    const sprite = new PIXI.Sprite(texture);
    sprite.x = x * tileWorldSize;
    sprite.y = y * tileWorldSize;
    sprite.width = tileWorldSize;
    sprite.height = tileWorldSize;

    this.container.addChild(sprite);
    this.activeTiles.set(key, { sprite, key, z, x, y });
  }

  /** Show a parent tile cropped to the area of the missing child tile. */
  private showFallbackParent(
    childZ: number,
    childX: number,
    childY: number,
    childWorldSize: number,
  ): void {
    // Walk up zoom levels to find a cached parent
    for (let pz = childZ - 1; pz >= this.config.minZoom; pz--) {
      const scale = Math.pow(2, childZ - pz);
      const px = Math.floor(childX / scale);
      const py = Math.floor(childY / scale);
      const parentKey = tileKeyStr(pz, px, py);

      const parentTexture = this.cache.get(parentKey);
      if (!parentTexture) continue;

      const fallbackKey = `fallback:${tileKeyStr(childZ, childX, childY)}`;
      if (this.activeTiles.has(fallbackKey)) return;

      // Compute the sub-rectangle of the parent tile that corresponds
      // to this child tile
      const subX = (childX % scale) / scale;
      const subY = (childY % scale) / scale;
      const subSize = 1 / scale;

      const frame = new PIXI.Rectangle(
        parentTexture.frame.x + subX * parentTexture.frame.width,
        parentTexture.frame.y + subY * parentTexture.frame.height,
        parentTexture.frame.width * subSize,
        parentTexture.frame.height * subSize,
      );

      const cropped = new PIXI.Texture(parentTexture.baseTexture, frame);
      const sprite = new PIXI.Sprite(cropped);
      sprite.x = childX * childWorldSize;
      sprite.y = childY * childWorldSize;
      sprite.width = childWorldSize;
      sprite.height = childWorldSize;

      this.container.addChild(sprite);
      this.activeTiles.set(fallbackKey, {
        sprite,
        key: fallbackKey,
        z: childZ,
        x: childX,
        y: childY,
      });
      return;
    }
  }

  /** Check if a tile key is a fallback parent still needed by a visible child. */
  private isParentFallback(key: string, neededKeys: Set<string>): boolean {
    if (!key.startsWith("fallback:")) return false;
    const childKey = key.slice("fallback:".length);
    return neededKeys.has(childKey) && !this.activeTiles.has(childKey);
  }

  /** Fetch a tile image from the server. */
  private async fetchTile(
    z: number,
    x: number,
    y: number,
    tileWorldSize: number,
    attempt = 0,
  ): Promise<void> {
    const key = tileKeyStr(z, x, y);

    // Throttle concurrent fetches
    if (this.pendingFetches.size >= this.maxConcurrent) return;

    const abort = new AbortController();
    this.pendingFetches.set(key, { abort, key });

    const url =
      this.config.tileUrl
        .replace("{z}", String(z))
        .replace("{x}", String(x))
        .replace("{y}", String(y)) +
      (this.config.tileCacheBust ? `?v=${this.config.tileCacheBust}` : "");

    try {
      const response = await fetch(url, { signal: abort.signal });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const imageBitmap = await createImageBitmap(blob);
      const texture = PIXI.Texture.from(
        imageBitmap as unknown as HTMLImageElement,
      );

      this.cache.set(key, texture);
      this.pendingFetches.delete(key);

      // Remove fallback sprite if it exists
      const fallbackKey = `fallback:${key}`;
      const fallback = this.activeTiles.get(fallbackKey);
      if (fallback) {
        this.container.removeChild(fallback.sprite);
        fallback.sprite.destroy();
        this.activeTiles.delete(fallbackKey);
      }

      // Add the real tile sprite
      this.addTileSprite(key, z, x, y, texture, tileWorldSize);
    } catch (err) {
      this.pendingFetches.delete(key);

      if (err instanceof DOMException && err.name === "AbortError") {
        return; // Expected - tile scrolled out of view
      }

      // Retry with backoff
      if (attempt < this.maxRetries) {
        setTimeout(
          () => this.fetchTile(z, x, y, tileWorldSize, attempt + 1),
          this.retryDelayMs * Math.pow(2, attempt),
        );
      } else {
        console.warn(`[TileManager] Failed to load tile ${key}:`, err);
        this.failedTiles.add(key);
      }
    }
  }

  /** Clean up all sprites and pending fetches. */
  destroy(): void {
    for (const [, pending] of this.pendingFetches) {
      pending.abort.abort();
    }
    this.pendingFetches.clear();

    for (const [, tile] of this.activeTiles) {
      this.container.removeChild(tile.sprite);
      tile.sprite.destroy();
    }
    this.activeTiles.clear();

    this.cache.clear();
    this.failedTiles.clear();
  }
}
